import React, { createContext, useContext, useState, useEffect } from 'react';
import bookingAPI from '../services/bookingApi';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize user session on app start
  useEffect(() => {
    initializeUserSession();
  }, []);

  const initializeUserSession = async () => {
    try {
      // console.log('🔄 UserContext: Starting session initialization...');
      setLoading(true);

      const response = await bookingAPI.getCurrentUser();
      // console.log('🌐 UserContext: getCurrentUser response:', response);

      if (response.success && response.data) {
        setUser(response.data);
        setIsAuthenticated(true);
        // console.log('✅ UserContext: User session restored:', response.data);
        // console.log('✅ UserContext: Authentication state - isAuthenticated:', true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
        // console.log('❌ UserContext: No active user session found');
        // console.log('❌ UserContext: Authentication state - isAuthenticated:', false);
      }
    } catch (error) {
      console.error('💥 UserContext: Error initializing user session:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
      // console.log('🏁 UserContext: Session initialization complete');
    }
  };

  const login = async (email, password) => {
    try {
      // console.log('🔑 UserContext: Login attempt for:', email);
      const response = await bookingAPI.userLogin(email, password);
      // console.log('🌐 UserContext: Login response:', response);

      if (response.success && response.data) {
        // console.log('✅ UserContext: Setting user state...');
        setUser(response.data);
        setIsAuthenticated(true);

        // Store user data in localStorage
        localStorage.setItem('userData', JSON.stringify(response.data));
        // console.log('💾 UserContext: User data stored in localStorage');
        // console.log('✅ UserContext: Login successful - isAuthenticated:', true);

        return { success: true, data: response.data };
      } else {
        // console.log('❌ UserContext: Login failed:', response.error);
        return {
          success: false,
          error: response.error || 'Login failed',
          message: response.message || 'Invalid credentials'
        };
      }
    } catch (error) {
      console.error('💥 UserContext: Login error:', error);
      return {
        success: false,
        error: 'Login failed',
        message: 'Unable to connect to the server'
      };
    }
  };

  const signup = async (userData) => {
    try {
      const response = await bookingAPI.userSignup(userData);
      
      if (response.success) {
        // console.log('User signed up successfully:', response.data);
        return { success: true, data: response.data };
      } else {
        return {
          success: false,
          error: response.error || 'Signup failed',
          message: response.message || 'Failed to create account'
        };
      }
    } catch (error) {
      console.error('Signup error:', error);
      return {
        success: false,
        error: 'Signup failed',
        message: 'Unable to connect to the server'
      };
    }
  };

  const logout = async () => {
    try {
      // console.log('🚪 Logout initiated...');

      // Call API logout (which clears all storage)
      // console.log('🌐 API logout result:', result);

      // Clear React state
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);

      // Target specific storage items to preserve other app data
      const keysToRemove = ['userData', 'authToken', 'user', 'token'];
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });

      // console.log('🧹 User logged out successfully - auth data cleared');

      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);

      // Even if server logout fails, clear everything locally
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);

      // Target specific storage items
      const keysToRemove = ['userData', 'authToken', 'user', 'token'];
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });

      // console.log('🧹 Force logout completed - auth data cleared');

      return { success: true };
    }
  };

  const updateUser = (updatedUserData) => {
    setUser(prevUser => ({
      ...prevUser,
      ...updatedUserData
    }));
    
    // Update localStorage
    const currentUserData = JSON.parse(localStorage.getItem('userData') || '{}');
    const updatedData = { ...currentUserData, ...updatedUserData };
    localStorage.setItem('userData', JSON.stringify(updatedData));
  };

  const refreshUser = async () => {
    try {
      const response = await bookingAPI.getCurrentUser();
      
      if (response.success && response.data) {
        setUser(response.data);
        setIsAuthenticated(true);
        localStorage.setItem('userData', JSON.stringify(response.data));
        return { success: true, data: response.data };
      } else {
        // If refresh fails, logout user
        await logout();
        return { success: false, message: 'Session expired' };
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
      // If refresh fails, logout user
      await logout();
      return { success: false, message: 'Failed to refresh session' };
    }
  };

  // Check if user has specific permissions or roles
  const hasPermission = (permission) => {
    if (!user || !isAuthenticated) return false;
    
    // If user has permissions array, check it
    if (user.permissions && Array.isArray(user.permissions)) {
      return user.permissions.includes(permission) || user.permissions.includes('all');
    }
    
    // Basic permission check based on user existence
    return true;
  };

  const isAdmin = () => {
    // console.log('CONTEXT DEBUG - isAdmin called');
    // console.log('CONTEXT DEBUG - user:', user);
    // console.log('CONTEXT DEBUG - isAuthenticated:', isAuthenticated);
    
    if (!user || !isAuthenticated) {
      // console.log('CONTEXT DEBUG - No user or not authenticated');
      return false;
    }
    
    const adminCheck = user.is_admin === true || 
                      user.role === 'admin' || 
                      user.role === 'Administrator' || 
                      user.role === 'Super Admin' || 
                      user.role === 'Placement Officer' || 
                      user.role === 'Head Office' || 
                      user.role === 'Hall Manager';
    
    // console.log('CONTEXT DEBUG - user.is_admin:', user.is_admin);
    // console.log('CONTEXT DEBUG - user.role:', user.role);
    // console.log('CONTEXT DEBUG - adminCheck result:', adminCheck);
    
    return adminCheck;
  };

  const contextValue = {
    // State
    user,
    loading,
    isAuthenticated,
    
    // Actions
    login,
    signup,
    logout,
    updateUser,
    refreshUser,
    initializeUserSession,
    
    // Utility functions
    hasPermission,
    isAdmin,
  };

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;